import { digitalTrendsCover, mobileRoiCover, seoTop3Cover } from './articleCovers';
import { useMemo, useState } from 'react';

import LeadForm from './LeadForm';
import PageMeta from './PageMeta';
import { RedesignFooter, RedesignHeader, RedesignPage, SiteLink } from './RedesignShell';

const categories = [
  'Все',
  'Разработка сайтов',
  'Мобильные приложения',
  'SEO',
  'AI / ML',
  'Мини-приложения',
  'CMS и админки',
  'Кейсы',
  'Digital-маркетинг',
];

const articles = [
  {
    href: '/blog/digital-trends-2024/',
    cover: digitalTrendsCover,
    tag: 'маркетинг',
    category: 'Digital-маркетинг',
    title: 'Топ-5 трендов digital-продвижения в 2026 году',
    description:
      'GEO вместо SEO, гиперперсонализация, короткое видео и UGC, продажи в мессенджерах, ИИ-автоматизация — разбираем 5 трендов digital-маркетинга 2026 года с цифрами и конкретными шагами',
  },
  {
    href: '/blog/mobile-roi/',
    cover: mobileRoiCover,
    tag: 'мобайл',
    category: 'Мобильные приложения',
    title: 'Как рассчитать ROI от разработки мобильного приложения для бизнеса',
    description:
      'Формулы, метрики и пример расчёта ROI мобильного приложения с реальными цифрами. Разбираем полные затраты, источники дохода, LTV, CAC и типичные ошибки, которые искажают окупаемость.',
  },
  {
    href: '/blog/seo-top3/',
    cover: seoTop3Cover,
    tag: 'seo',
    category: 'SEO',
    title: 'Кейс: как мы вывели интернет-магазин в ТОП-3 за 6 месяцев',
    description:
      'Реальный кейс Fatality Studio: разработка интернет-магазина техники с нуля, SEO-продвижение и реклама. Рост органического трафика на 40% за 3 месяца, цена заказа 1500 ₽, выход в ТОП по коммерческим запросам.',
  },
];

export const BLOG_PAGE_SIZE = 9;

export function getNextArticleCount(currentCount, totalCount) {
  return Math.min(currentCount + BLOG_PAGE_SIZE, totalCount);
}

function SectionHeader({ eyebrow, children, description, dark = false }) {
  return (
    <div className={`sec-head${dark ? ' ink-head' : ''}`}>
      <div>
        <div className={`eyebrow${dark ? ' cyan' : ''}`}>— {eyebrow}</div>
        <h2>{children}</h2>
      </div>
      {description && <p className="sub">{description}</p>}
    </div>
  );
}

function BlogRedesignPage() {
  const [activeCategory, setActiveCategory] = useState('Все');
  const [visibleCount, setVisibleCount] = useState(BLOG_PAGE_SIZE);
  const filteredArticles = useMemo(() => (
    activeCategory === 'Все'
      ? articles
      : articles.filter((article) => article.category === activeCategory)
  ), [activeCategory]);
  const visibleArticles = filteredArticles.slice(0, visibleCount);
  const hasMoreArticles = visibleCount < filteredArticles.length;

  const selectCategory = (category) => {
    setActiveCategory(category);
    setVisibleCount(BLOG_PAGE_SIZE);
  };

  return (
    <RedesignPage>
      <PageMeta
        title="Блог Fatality Studio — статьи о разработке сайтов, приложений и digital-маркетинге"
        description="Полезные статьи от команды Fatality Studio: разработка сайтов и мобильных приложений, AI/ML, digital-маркетинг, кейсы и инсайты из практики агентства"
      />
      <RedesignHeader />

      <section className="ink hero" data-screen-label="blog hero">
        <div
          className="glow"
          style={{ right: '-360px', top: '-360px', width: '1000px', height: '1000px', opacity: 0.1 }}
        />
        <div className="container" style={{ position: 'relative' }}>
          <div className="eyebrow cyan">— блог</div>
          <h1>
            <span className="accent">Блог</span>
          </h1>
          <p className="hero-sub">Делимся опытом, кейсами и полезными материалами из практики агентства</p>
          <div className="hero-meta blog-hero-meta">
            {categories.slice(1, 5).map((item, index) => (
              <div key={item}>
                <div className="num">
                  {String(index + 1).padStart(2, '0')}
                  <span>/</span>
                </div>
                <div className="lbl">{item}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="canvas sec-pad" data-screen-label="blog categories">
        <div className="container">
          <SectionHeader eyebrow="фильтр">Категории</SectionHeader>
          <div className="category-filter blog-filter" aria-label="Категории блога">
            {categories.map((item) => (
              <button
                aria-pressed={activeCategory === item}
                className={`category-chip${activeCategory === item ? ' active' : ''}`}
                onClick={() => selectCategory(item)}
                type="button"
                key={item}
              >
                {item}
              </button>
            ))}
          </div>
          <div className="blog-grid blog-article-list">
            {visibleArticles.map((article) => (
              <div className="article" key={article.href}>
                {article.cover && (
                  <SiteLink href={article.href} className="article-img" aria-label={article.title}>
                    <img src={article.cover} alt="" width="1800" height="1200" loading="lazy" decoding="async" />
                  </SiteLink>
                )}
                <div className="article-meta">
                  <button
                    className="article-tag"
                    onClick={() => selectCategory(article.category)}
                    type="button"
                  >
                    {article.tag}
                  </button>
                  {article.date && <span className="article-date">{article.date}</span>}
                  {article.readTime && <span className="article-read-time">{article.readTime}</span>}
                </div>
                <SiteLink href={article.href}>
                  <h3 className="article-title">{article.title}</h3>
                </SiteLink>
                <p className="article-desc">{article.description}</p>
                <SiteLink href={article.href} className="link-flat article-read-more">
                  Читать далее →
                </SiteLink>
              </div>
            ))}
          </div>
          {/* TODO(brand-redesign): add 6–9 more articles after source-backed titles, descriptions, categories, dates, read times and covers are provided. */}
          {hasMoreArticles && (
            <div className="blog-pagination">
              <button
                className="btn btn-ghost-dark blog-load-more"
                onClick={() => setVisibleCount((count) => getNextArticleCount(count, filteredArticles.length))}
                type="button"
              >
                Загрузить ещё
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="ink lead" id="blog-lead" data-screen-label="blog lead">
        <div
          className="glow"
          style={{ left: '-300px', top: '-200px', width: '700px', height: '700px', opacity: 0.1 }}
        />
        <div className="container" style={{ position: 'relative' }}>
          <div className="lead-grid">
            <div className="lead-side">
              <div className="eyebrow cyan">— cta</div>
              <div className="lead-heading">Есть идея, проект или хотите познакомиться?</div>
            </div>
            <LeadForm
              formId="blog-lead-form"
              idPrefix="blog"
              contactLabel="Телефон"
              contactType="tel"
              contactPlaceholder="+7 (___) ___-__-__"
              showCompany={false}
              submitLabel="Отправить"
              taskPlaceholder="Есть идея, проект или хотите познакомиться?"
            />
          </div>
        </div>
      </section>

      <RedesignFooter />
    </RedesignPage>
  );
}

export default BlogRedesignPage;
